import { useAuth0 } from "@auth0/auth0-react";
import { Box, Alert, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { AUTH0_AUDIENCE } from "./env";
import { useEffect, useState } from "react";

export default function PlatformGuard({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently } = useAuth0();
  const [status, setStatus] = useState<"loading" | "ok" | "forbidden">("loading");

  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH0_AUDIENCE || undefined },
        });
        // Decode the JWT payload (base64url) to check zennet_role claim
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        // The namespace is injected as a claim — check any key containing zennet_role
        const role = Object.entries(payload).find(([k]) => k.endsWith("/zennet_role"))?.[1];
        setStatus(role === "platform_admin" ? "ok" : "forbidden");
      } catch {
        setStatus("forbidden");
      }
    })();
  }, [getAccessTokenSilently]);

  if (status === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  if (status === "forbidden") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          <strong>Access denied.</strong> Your account does not have the <code>platform_admin</code> role
          required to access the Zennet Admin Portal.
          Contact your Zennet administrator.
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
}
